import React, { useState, useEffect } from 'react';
import { branchAPI, tenantAPI, planAPI, templateAPI, pmsAPI, branchPageAPI } from '../../services/api';
import { branchUploadAPI } from '../../services/uploadAPI';
import { useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import LogoUpload from '../../components/LogoUpload/LogoUpload';
import PmsProviderFields from '../../components/PmsProviderFields/PmsProviderFields';
import DataTable from '../../components/DataTable/DataTable';
import '../Dashboard/Dashboard.css';
import './Branches.css';

// HotelKey / InnKey keys are write-only: they load blank (with `<key>Set`) and
// a blank field keeps the saved key when the branch is saved.
const EMPTY_HOTELKEY = {
    serverUrl: '', appId: '', appSecret: '', propertyId: '', propertyCode: '', routesTo: 'rooms', syncInterval: 1,
    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
};
const EMPTY_INNKEY = {
    serverUrl: '', partner: '', accessKey: '', sharedKey: '', routesTo: 'rooms', syncInterval: 5,
    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
};

// Internet before sign-in (no mobile network): a few slow minutes for the WhatsApp
// code and a UPI payment. MikroTik only — Push Config sets the router up.
const PRELOGIN_DEFAULTS = { enabled: false, minutes: 5, speedKbps: 1024, perDay: 3 };

// How long a sign-in lasts, in minutes (RADIUS Session-Timeout / Idle-Timeout): blank
// = the router's own setting, 0 = none. 0 and 0 = permanent (until the plan / stay ends).
const SESSION_LIMIT_DEFAULTS = { sessionMinutes: '', idleMinutes: '' };
const sessionLimitsOf = (s) => ({
    sessionMinutes: s?.sessionMinutes == null ? '' : s.sessionMinutes,
    idleMinutes: s?.idleMinutes == null ? '' : s.idleMinutes,
});
// The portal's one "otp" method is two boxes here (SMS OTP, WhatsApp OTP). An older
// branch saved the bare 'otp' value, which no box shows: read as both boxes ticked,
// and never saved again.
const normalizeMethods = (methods) => {
    const list = Array.isArray(methods) ? methods : [];
    if (!list.includes('otp')) return list;
    return [...new Set([...list.filter(m => m !== 'otp'), 'sms_otp', 'whatsapp_otp'])];
};

// The plan each login method gives at this branch ('' = as before: its guest / OTP
// plan). A room's own plan comes first; a voucher always carries its own.
const METHOD_PLAN_ROWS = [
    { key: 'pms',          label: 'PMS (room + last name)', method: 'pms',          asNow: "branch's guest plan" },
    { key: 'sms_otp',      label: 'SMS OTP',                method: 'sms_otp',      asNow: "branch's OTP plan" },
    { key: 'whatsapp_otp', label: 'WhatsApp OTP',           method: 'whatsapp_otp', asNow: "branch's OTP plan" },
    { key: 'resident',     label: 'Resident',               method: 'resident',     asNow: "branch's resident plan" },
    { key: 'staff',        label: 'Staff',                  method: 'staff',        asNow: "branch's staff plan" },
];
// Ticked methods whose plan is set somewhere else — said so beside the choices.
const METHOD_PLAN_ELSEWHERE = [
    { methods: ['voucher', 'access_code'], label: 'Voucher / Access Code', note: 'the plan of the voucher\'s batch, chosen when the vouchers are made' },
    { methods: ['mac'], label: 'MAC Auth (bypass)', note: 'the plan set on the device (Devices)' },
    { methods: ['username_password', 'email_otp', 'qr', 'social'], label: 'Username & Password, Email OTP, QR, Social', note: 'no sign-in form on the portal yet, so nobody signs in this way' },
];
const methodPlansOf = (m) => Object.fromEntries(METHOD_PLAN_ROWS.map(({ key }) => [key, m?.[key]?._id || m?.[key] || '']));
const methodPlansForSave = (m = {}) => Object.fromEntries(METHOD_PLAN_ROWS.map(({ key }) => [key, m[key] || null]));
const planSpeed = (p) => {
    const kbps = p?.bandwidth?.downloadSpeed;
    if (!kbps) return '';
    return kbps >= 1024 ? ` — ${+(kbps / 1024).toFixed(1)} Mbps` : ` — ${kbps} kbps`;
};

const sessionLimitsForSave = (s = {}) => {
    const num = (v) => (v === '' || v == null ? null : Math.max(0, Number(v) || 0));
    return { sessionMinutes: num(s.sessionMinutes), idleMinutes: num(s.idleMinutes) };
};
const preLoginForSave = (p = {}) => {
    const within = (value, low, high, fallback) => Math.min(high, Math.max(low, parseInt(value, 10) || fallback));
    return {
        enabled: !!p.enabled,
        minutes: within(p.minutes, 1, 30, 5),
        speedKbps: within(p.speedKbps, 64, 10240, 1024),
        perDay: within(p.perDay, 1, 10, 3),
    };
};

const Branches = () => {
    const admin = useAuthStore((state) => state.admin);
    const [branches, setBranches] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [plans, setPlans] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [modalBranchPages, setModalBranchPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalTab, setModalTab] = useState('general');
    const [editingBranch, setEditingBranch] = useState(null);
    const [filterTenantId, setFilterTenantId] = useState('');
    const [viewingBranch, setViewingBranch] = useState(null);

    // Delete Confirmation State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState(null);

    const [formData, setFormData] = useState({
        tenantId: '',
        name: '',
        companyName: '',
        code: '',
        loginPolicy: {
            methods: ['sms_otp', 'whatsapp_otp'],
            defaultMethod: 'otp',
            requireTerms: true,
            voucherMode: 'tab',
            otpDefaultMethod: 'whatsapp',
            otpMaxDevices: 1,
            preLogin: { ...PRELOGIN_DEFAULTS },
            sessionLimits: { ...SESSION_LIMIT_DEFAULTS },
            termsUrl: '',
            privacyUrl: '',
            methodOrder: [],
            methodLabels: {},
        },
        roomOps: {
            autoCheckoutManualHours: '',
        },
        defaultPlanId: '',
        templateId: '',
        limits: {
            maxConcurrentUsers: 100,
            maxDevicesPerUser: 3
        },
        integrations: {
            pms: {
                enabled: false,
                provider: 'capitaland',
                apiUrl: '',
                apiKey: ''
            },
            capitaland: {
                clientId: '',
                clientSecret: '',
                terminalId: '',
                propertyCode: '',
                environment: 'production',
                roomAuthSuffix: 'ORNRP',
                routesTo: 'rooms',
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false
            },
            wincloud: {
                serverUrl:    '',
                propNm:       '',
                cid:          '',
                apiKey:       '',
                syncInterval: 5,
                routesTo:     'rooms',
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false,
            },
            winhms: {
                serverUrl:    '',
                hotelCode:    '',
                username:     '',
                password:     '',
                syncInterval: 5,
                routesTo:     'rooms',
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false,
            },
            idsnext: {
                serverUrl:    '',
                hotelCode:    '',
                username:     '',
                password:     '',
                syncInterval: 5,
                routesTo:     'rooms',
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false,
            },
            hotla: {
                serverUrl: 'https://hotla.com/hotlaapi/wifiapi.php',
                propNm:    '',
                apiKey:    '',
                routesTo:  'rooms',
                syncInterval: 5,
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false,
            },
            hotelkey: { ...EMPTY_HOTELKEY },
            innkey:   { ...EMPTY_INNKEY },
            fias: {
                propertyCode: '',
                guestSync: true,
                roomSync: true,
                routesTo: 'rooms',
                maxDevicesPerRoom: null,
                ntfyTopic: '',
                manualApproval: false,
            },
        },
        branding: {
            primaryColor: '#3b82f6',
            secondaryColor: '#1e3a8a',
            logo: { url: '', fileName: '' },
            backgroundImage: { url: '', fileName: '' }
        },
        welcomeMessage: 'Welcome to our WiFi Network',
        termsAndConditions: '',
        privacyPolicy: '',
        redirectURL: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Only superadmin and tenant_admin can list all tenants
            const canReadTenants = admin?.role === 'superadmin' || admin?.role === 'tenant_admin';

            const [branchesResult, tenantsResult, templatesResult] = await Promise.allSettled([
                branchAPI.getAll(null, { limit: 1000 }),
                canReadTenants ? tenantAPI.getAll({ limit: 1000 }) : Promise.resolve(null),
                templateAPI.getAll({ limit: 1000 })
            ]);

            if (branchesResult.status === 'fulfilled') {
                setBranches(branchesResult.value.data.data.branches || []);
            } else {
                toast.error('Failed to fetch branches');
            }

            const templatesRes = templatesResult.status === 'fulfilled' ? templatesResult.value : null;
            const tenantsRes   = tenantsResult.status  === 'fulfilled' ? tenantsResult.value  : null;

            setTemplates(templatesRes?.data?.data || []);

            let tenantList = [];
            if (tenantsRes) {
                tenantList = tenantsRes.data.data.tenants;
                setTenants(tenantList);
            } else if (admin?.tenantId) {
                // branch_admin — scope to their own tenant
                tenantList = [{ _id: admin.tenantId, name: admin.tenantName || 'My Organization' }];
                setTenants(tenantList);
                setFormData(prev => ({ ...prev, tenantId: admin.tenantId }));
                fetchPlans(admin.tenantId);
            }

            // Auto-select tenant if only one available (for Tenant Admins)
            if (tenantList.length === 1 && !formData.tenantId) {
                setFormData(prev => ({ ...prev, tenantId: tenantList[0]._id }));
                fetchPlans(tenantList[0]._id);
            }
        } catch (error) {
            toast.error('Unexpected error loading data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPlans = async (tenantId, branchId) => {
        try {
            const response = await planAPI.getAll(branchId);
            setPlans(response.data.data.plans);
        } catch (error) {
            console.error('Failed to fetch plans');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            const parts = name.split('.');
            setFormData(prev => {
                const updated = { ...prev };
                let current = updated;
                for (let i = 0; i < parts.length - 1; i++) {
                    // Ensure nested objects exist
                    if (!current[parts[i]]) current[parts[i]] = {};
                    current = current[parts[i]];
                }
                current[parts[parts.length - 1]] = type === 'checkbox' ? checked : value;
                return updated;
            });
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));

            if (name === 'name') {
                setFormData(prev => ({
                    ...prev,
                    code: value.toUpperCase().replace(/\s+/g, '').substring(0, 10)
                }));
            }

            if (name === 'tenantId' && value) {
                fetchPlans(value, editingBranch?._id);
            }
        }
    };

    const handleMethodToggle = (method) => {
        setFormData(prev => ({
            ...prev,
            loginPolicy: {
                ...prev.loginPolicy,
                methods: prev.loginPolicy.methods.includes(method)
                    ? prev.loginPolicy.methods.filter(m => m !== method)
                    : [...prev.loginPolicy.methods, method]
            }
        }));
    };

    const loadSystemDefaults = async () => {
        if (loadingDefaults) return;
        setLoadingDefaults(true);
        try {
            const res = await pmsAPI.getSystemDefaults();
            if (!res.data.configured) {
                toast.warn('No CapitaLand credentials configured in server environment');
                return;
            }
            const d = res.data.data;
            setFormData(prev => ({
                ...prev,
                integrations: {
                    ...prev.integrations,
                    pms: { ...prev.integrations.pms, provider: 'capitaland', enabled: true },
                    capitaland: {
                        ...prev.integrations.capitaland,
                        clientId:       d.clientId       || '',
                        clientSecret:   d.clientSecret   || '',
                        terminalId:     d.terminalId     || '',
                        propertyCode:   d.propertyCode   || '',
                        environment:    d.environment    || 'production',
                        roomAuthSuffix: d.roomAuthSuffix  || 'ORNRP',
                    },
                },
            }));
            toast.success('System credentials loaded — review and save');
        } catch {
            toast.error('Failed to load system credentials');
        } finally {
            setLoadingDefaults(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.loginPolicy.methods.length === 0) {
            toast.error('Please select at least one login method');
            return;
        }

        // Auto-add 'pms' to login methods when CapitaLand PMS is enabled
        let submitData = {
            ...formData,
            loginPolicy: {
                ...formData.loginPolicy,
                methods: normalizeMethods(formData.loginPolicy.methods),
                // A cleared box is 1 device, never "unlimited" by accident.
                otpMaxDevices: formData.loginPolicy.otpMaxDevices === null
                    ? null
                    : Math.max(1, parseInt(formData.loginPolicy.otpMaxDevices, 10) || 1),
                preLogin: preLoginForSave(formData.loginPolicy.preLogin),
                sessionLimits: sessionLimitsForSave(formData.loginPolicy.sessionLimits),
                methodPlans: methodPlansForSave(formData.loginPolicy.methodPlans),
            },
            roomOps: {
                ...formData.roomOps,
                autoCheckoutManualHours: formData.roomOps?.autoCheckoutManualHours === '' || formData.roomOps?.autoCheckoutManualHours == null
                    ? null
                    : Number(formData.roomOps.autoCheckoutManualHours),
            },
        };
        // Auto-add 'pms' to login methods whenever ANY PMS provider is
        // enabled — previously capitaland-only, which meant an admin who
        // configured WinCloud/WinHMS/IDS Next/HOTLA (entered credentials,
        // rooms synced fine) could still leave "PMS Login" unchecked on the
        // captive portal without realizing it, silently hiding the Room
        // Number + Last Name form guests need. The landing page form itself
        // (Landing.jsx) is already fully generic — roomNumber/lastName is
        // the same for every provider — this was the only provider-specific
        // gap.
        if (
            submitData.integrations?.pms?.enabled &&
            submitData.integrations?.pms?.provider &&
            !submitData.loginPolicy.methods.includes('pms')
        ) {
            submitData = {
                ...submitData,
                loginPolicy: {
                    ...submitData.loginPolicy,
                    methods: [...submitData.loginPolicy.methods, 'pms'],
                },
            };
        }

        try {
            if (editingBranch) {
                await branchAPI.update(editingBranch._id, submitData);
                toast.success('Branch updated successfully');
            } else {
                await branchAPI.create(submitData);
                toast.success('Branch created successfully');
            }

            setShowModal(false);
            setEditingBranch(null);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleEdit = async (branch) => {
        setEditingBranch(branch);
        setModalTab('general');
        setFormData({
            tenantId: branch.tenantId?._id || branch.tenantId || '',
            name: branch.name,
            companyName: branch.companyName || '',
            code: branch.code,
            loginPolicy: {
                methods: ['sms_otp', 'whatsapp_otp'],
                defaultMethod: 'otp',
                requireTerms: true,
                voucherMode: 'tab',
                otpDefaultMethod: 'whatsapp',
                otpMaxDevices: 1,
                methodOrder: [],
                methodLabels: {},
                ...(branch.loginPolicy || {}),
                methods: normalizeMethods(branch.loginPolicy?.methods),
                preLogin: { ...PRELOGIN_DEFAULTS, ...(branch.loginPolicy?.preLogin || {}) },
                sessionLimits: sessionLimitsOf(branch.loginPolicy?.sessionLimits),
                methodPlans: methodPlansOf(branch.loginPolicy?.methodPlans),
            },
            roomOps: {
                autoCheckoutManualHours: branch.roomOps?.autoCheckoutManualHours ?? '',
            },
            defaultPlanId: (branch.defaultPlanId?._id || branch.defaultPlanId || ''),
            templateId: (branch.templateId?._id || branch.templateId || ''),
            limits: {
                maxConcurrentUsers: 100,
                maxDevicesPerUser: 3,
                ...(branch.limits || {})
            },
            integrations: {
                pms: {
                    enabled: false,
                    provider: 'capitaland',
                    apiUrl: '',
                    apiKey: '',
                    ...(branch.integrations?.pms || {})
                },
                capitaland: {
                    clientId: '',
                    clientSecret: '',
                    terminalId: '',
                    propertyCode: '',
                    environment: 'production',
                    roomAuthSuffix: 'ORNRP',
                    routesTo: 'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.capitaland || {})
                },
                wincloud: {
                    serverUrl:    '',
                    propNm:       '',
                    cid:          '',
                    apiKey:       '',
                    syncInterval: 5,
                    routesTo:     'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.wincloud || {})
                },
                winhms: {
                    serverUrl:    '',
                    hotelCode:    '',
                    username:     '',
                    password:     '',
                    syncInterval: 5,
                    routesTo:     'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.winhms || {})
                },
                idsnext: {
                    serverUrl:    '',
                    hotelCode:    '',
                    username:     '',
                    password:     '',
                    syncInterval: 5,
                    routesTo:     'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.idsnext || {})
                },
                hotla: {
                    serverUrl: 'https://hotla.com/hotlaapi/wifiapi.php',
                    propNm:    '',
                    apiKey:    '',
                    routesTo:  'rooms',
                    syncInterval: 5,
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.hotla || {})
                },
                hotelkey: { ...EMPTY_HOTELKEY, ...(branch.integrations?.hotelkey || {}), appSecret: '' },
                innkey:   { ...EMPTY_INNKEY, ...(branch.integrations?.innkey || {}), accessKey: '', sharedKey: '' },
                fias: {
                    propertyCode: '',
                    guestSync: true,
                    roomSync: true,
                    routesTo: 'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false,
                    ...(branch.integrations?.fias || {})
                },
            },
            branding: {
                primaryColor: '#3b82f6',
                secondaryColor: '#1e3a8a',
                logo: { url: '', fileName: '' },
                backgroundImage: { url: '', fileName: '' },
                ...(branch.branding || {})
            },
            welcomeMessage: branch.welcomeMessage || 'Welcome to our WiFi Network',
            termsAndConditions: branch.termsAndConditions || '',
            privacyPolicy: branch.privacyPolicy || '',
            redirectURL: branch.redirectURL || '',
        });

        if (branch.tenantId) {
            await fetchPlans(branch.tenantId._id || branch.tenantId, branch._id);
        }

        try {
            const res = await branchPageAPI.getAll(branch._id);
            setModalBranchPages(res.data.data || []);
        } catch {
            setModalBranchPages([]);
        }

        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!branchToDelete) return;
        try {
            await branchAPI.delete(branchToDelete._id);
            toast.success('Branch deleted successfully');
            fetchData();
            setShowDeleteModal(false);
            setBranchToDelete(null);
        } catch (error) {
            toast.error('Failed to delete branch');
        }
    };

    const handleDelete = (branch) => {
        setBranchToDelete(branch);
        setShowDeleteModal(true);
    };

    const handleGenerateKeys = async (id) => {
        try {
            const response = await branchAPI.generateApiKeys(id);
            const { apiKey, apiSecret } = response.data.data;
            alert(`API Credentials Generated:\n\nAPI Key: ${apiKey}\nAPI Secret: ${apiSecret}\n\n⚠️ Save these credentials securely. The secret won't be shown again.`);
            fetchData();
        } catch (error) {
            toast.error('Failed to generate API keys');
        }
    };

    const handleLogoUpload = async (file) => {
        if (!editingBranch) {
            toast.error('Please save the branch first');
            return;
        }
        try {
            await branchUploadAPI.uploadLogo(editingBranch._id, file);
            toast.success('Logo uploaded successfully');
            fetchData(); // Refresh to get new URL
        } catch (error) {
            toast.error('Failed to upload logo');
        }
    };

    const handleBackgroundUpload = async (file) => {
        if (!editingBranch) {
            toast.error('Please save the branch first');
            return;
        }
        try {
            await branchUploadAPI.uploadBackground(editingBranch._id, file);
            toast.success('Background image uploaded successfully');
            fetchData(); // Refresh to get new URL
        } catch (error) {
            toast.error('Failed to upload background image');
        }
    };

    const resetForm = () => {
        setFormData({
            tenantId: '',
            name: '',
            companyName: '',
            code: '',
            loginPolicy: {
                methods: ['sms_otp', 'whatsapp_otp'],
                defaultMethod: 'otp',
                requireTerms: true,
                voucherMode: 'tab',
                otpDefaultMethod: 'whatsapp',
                otpMaxDevices: 1,
                preLogin: { ...PRELOGIN_DEFAULTS },
                termsUrl: '',
                privacyUrl: '',
                methodOrder: [],
                methodLabels: {},
            },
            roomOps: {
                autoCheckoutManualHours: '',
            },
            defaultPlanId: '',
            templateId: '',
            limits: {
                maxConcurrentUsers: 100,
                maxDevicesPerUser: 3
            },
            integrations: {
                pms: {
                    enabled: false,
                    provider: 'capitaland',
                    apiUrl: '',
                    apiKey: ''
                },
                capitaland: {
                    clientId: '',
                    clientSecret: '',
                    terminalId: '',
                    propertyCode: '',
                    environment: 'production',
                    roomAuthSuffix: 'ORNRP',
                    routesTo: 'rooms',
                    maxDevicesPerRoom: null,
                    ntfyTopic: '',
                    manualApproval: false
                },
                wincloud: {
                    serverUrl: '', propNm: '', cid: '', apiKey: '', syncInterval: 5, routesTo: 'rooms',
                    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
                },
                winhms: {
                    serverUrl: '', hotelCode: '', username: '', password: '', syncInterval: 5, routesTo: 'rooms',
                    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
                },
                idsnext: {
                    serverUrl: '', hotelCode: '', username: '', password: '', syncInterval: 5, routesTo: 'rooms',
                    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
                },
                hotla: {
                    serverUrl: 'https://hotla.com/hotlaapi/wifiapi.php', propNm: '', apiKey: '', routesTo: 'rooms', syncInterval: 5,
                    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
                },
                hotelkey: { ...EMPTY_HOTELKEY },
                innkey:   { ...EMPTY_INNKEY },
                fias: {
                    propertyCode: '', guestSync: true, roomSync: true, routesTo: 'rooms',
                    maxDevicesPerRoom: null, ntfyTopic: '', manualApproval: false,
                },
            },
            branding: {
                primaryColor: '#3b82f6',
                secondaryColor: '#1e3a8a',
                logo: { url: '', fileName: '' },
                backgroundImage: { url: '', fileName: '' }
            },
            welcomeMessage: 'Welcome to our WiFi Network',
            termsAndConditions: '',
            privacyPolicy: '',
            redirectURL: ''
        });
        setModalTab('general');

        // Auto-populate tenantId for non-superadmins
        if (admin && admin.role !== 'superadmin' && admin.tenantId) {
            const tId = typeof admin.tenantId === 'object' ? admin.tenantId._id : admin.tenantId;
            setFormData(prev => ({ ...prev, tenantId: tId }));
            fetchPlans(tId);
        }

        setPlans([]);
    };

    const filteredBranches = branches.filter(branch => {
        const tenantId = branch.tenantId?._id || branch.tenantId;
        return !filterTenantId || tenantId === filterTenantId;
    });

    const isSuperAdmin = admin?.role === 'superadmin';

    const METHOD_META = {
        pms: { label:'PMS', icon:'fa-hotel' },
        voucher: { label:'Voucher', icon:'fa-ticket-alt' },
        access_code: { label:'Access Code', icon:'fa-key' },
        username_password: { label:'User+Pass', icon:'fa-user-lock' },
        sms_otp: { label:'SMS OTP', icon:'fa-sms' },
        whatsapp_otp: { label:'WhatsApp', icon:'fa-whatsapp' },
        email_otp: { label:'Email OTP', icon:'fa-envelope' },
        qr: { label:'QR', icon:'fa-qrcode' },
        mac: { label:'MAC Auth', icon:'fa-network-wired' },
        social: { label:'Social', icon:'fa-share-alt' },
        otp: { label:'OTP', icon:'fa-mobile-alt' },
    };

    const methodBadges = (branch) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {(branch.loginPolicy?.methods || []).map(method => {
                const m = METHOD_META[method] || { label: method.toUpperCase(), icon: 'fa-circle' };
                return (
                    <span key={method} style={{ fontSize: '0.7rem', padding: '2px 8px', background: '#ede9fe', color: '#5b21b6', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <i className={`fas ${m.icon}`} style={{ fontSize: '0.65rem' }}></i>
                        {m.label}
                    </span>
                );
            })}
        </div>
    );

    const BRANCH_COLUMNS = [
        {
            label: 'Branch', field: 'name', filter: true,
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <i className="fas fa-code-branch" style={{ color: '#7e22ce' }}></i>
                    <div>
                        <div style={{ fontWeight: 600 }}>{row.branch.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.branch.code}</div>
                    </div>
                </div>
            ),
            searchValue: (row) => `${row.branch.name} ${row.branch.code}`,
        },
        { label: 'Capacity', field: 'capacityText' },
        { label: 'Device Limit', field: 'deviceLimitText' },
        { label: 'Login Methods', field: 'methodsText', sortable: false, render: (row) => methodBadges(row.branch) },
        {
            label: 'Status', field: 'status', filter: { options: ['active', 'inactive'] },
            render: (row) => (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <span className={`status-badge ${row.status === 'active' ? 'active' : 'inactive'}`}>
                        <span className="badge-dot"></span>{row.status.toUpperCase()}
                    </span>
                    {row.branch.apiEnabled && (
                        <span className="status-badge" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
                            <i className="fas fa-code" style={{ marginRight: '4px' }}></i> API
                        </span>
                    )}
                </div>
            ),
        },
        {
            label: 'Action', field: 'action', sortable: false, searchValue: () => '',
            render: (row) => (
                <div className="table-row-actions">
                    <button className="action-btn-icon view" onClick={() => setViewingBranch(row.branch)} title="View"><i className="fas fa-eye"></i></button>
                    <button className="action-btn-icon edit" onClick={() => handleEdit(row.branch)} title="Edit"><i className="fas fa-edit"></i></button>
                    <button className="action-btn-icon delete" onClick={() => handleDelete(row.branch)} title="Delete"><i className="fas fa-trash"></i></button>
                </div>
            ),
        },
    ];

    const branchRows = (list) => list.map((branch) => ({
        id: branch._id,
        branch,
        capacityText: `${branch.limits?.maxConcurrentUsers || '100+'} Users`,
        deviceLimitText: `${branch.limits?.maxDevicesPerUser || '3'} / User`,
        methodsText: (branch.loginPolicy?.methods || []).join(' '),
        status: branch.status || 'active',
    }));

    // Group branches by tenant for superadmin "All Companies" view
    const groupedBranches = isSuperAdmin && !filterTenantId
        ? filteredBranches.reduce((acc, branch) => {
            const tid   = branch.tenantId?._id || branch.tenantId || 'unknown';
            const tname = branch.tenantId?.name || 'Unknown Company';
            if (!acc[tid]) acc[tid] = { name: tname, branches: [] };
            acc[tid].branches.push(branch);
            return acc;
        }, {})
        : null;

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-network-wired"></i>
                            Branches
                        </h1>
                        <p className="page-subtitle-modern">Manage branch locations and configurations</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={() => { resetForm(); setModalBranchPages([]); setShowModal(true); }}>
                            <i className="fas fa-plus"></i> Add Branch
                        </button>
                    </div>
                </div>
            </div>

            {isSuperAdmin && tenants.length > 0 && (
                <div className="filters-section">
                    <select
                        className="filter-select-modern"
                        value={filterTenantId}
                        onChange={(e) => setFilterTenantId(e.target.value)}
                    >
                        <option value="">All Companies ({branches.length})</option>
                        {tenants.map(t => {
                            const count = branches.filter(b => (b.tenantId?._id || b.tenantId) === t._id).length;
                            return (
                                <option key={t._id} value={t._id}>
                                    {t.name} ({count})
                                </option>
                            );
                        })}
                    </select>
                </div>
            )}

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading branches...</p>
                </div>
            ) : filteredBranches.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-building"></i>
                    </div>
                    <h3>No Branches Found</h3>
                    <p>Get started by adding your first branch location.</p>
                </div>
            ) : groupedBranches ? (
                <div>
                    {Object.entries(groupedBranches).map(([tid, group]) => (
                        <div key={tid} className="ad-card" style={{ marginBottom: '1.5rem' }}>
                            <div className="ad-card-header">
                                <div className="ad-card-title">
                                    <i className="fas fa-building"></i>
                                    <h2>{group.name}</h2>
                                    <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 400, marginLeft: '8px' }}>
                                        {group.branches.length} branch{group.branches.length !== 1 ? 'es' : ''}
                                    </span>
                                </div>
                                <button
                                    className="btn-primary-modern btn-sm"
                                    onClick={() => {
                                        resetForm();
                                        setFormData(prev => ({ ...prev, tenantId: tid }));
                                        fetchPlans(tid);
                                        setShowModal(true);
                                    }}
                                >
                                    <i className="fas fa-plus"></i> Add Branch
                                </button>
                            </div>
                            <div style={{ padding: '0 1.375rem 1.375rem' }}>
                                <DataTable columns={BRANCH_COLUMNS} rows={branchRows(group.branches)} emptyMessage="No branches" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="ad-card">
                    <div className="ad-card-header">
                        <div className="ad-card-title">
                            <i className="fas fa-code-branch"></i>
                            <h2>Branches</h2>
                        </div>
                    </div>
                    <div style={{ padding: '0 1.375rem 1.375rem' }}>
                        <DataTable columns={BRANCH_COLUMNS} rows={branchRows(filteredBranches)} emptyMessage="No branches" />
                    </div>
                </div>
            )}

            {/* Branch Detail (View) Modal */}
            {viewingBranch && (
                <div className="modal-overlay-modern" onClick={() => setViewingBranch(null)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{viewingBranch.name}</h2>
                            </div>
                            <button className="modal-close" onClick={() => setViewingBranch(null)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="status-badges">
                                <span className={`status-badge ${(viewingBranch.status || 'active') === 'active' ? 'active' : 'inactive'}`}>
                                    <span className="badge-dot"></span>
                                    {(viewingBranch.status || 'active').toUpperCase()}
                                </span>
                                {viewingBranch.apiEnabled && (
                                    <span className="status-badge" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
                                        <i className="fas fa-code" style={{ marginRight: '4px' }}></i> API
                                    </span>
                                )}
                            </div>
                            <div className="info-grid two-col">
                                {isSuperAdmin && (
                                    <div className="info-item">
                                        <i className="fas fa-building"></i>
                                        <div>
                                            <span className="info-label">Company</span>
                                            <span className="info-value">{viewingBranch.tenantId?.name || 'N/A'}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="info-item">
                                    <i className="fas fa-hashtag"></i>
                                    <div><span className="info-label">Branch Code</span><span className="info-value">{viewingBranch.code}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-users"></i>
                                    <div>
                                        <span className="info-label">Capacity</span>
                                        <span className="info-value">{viewingBranch.limits?.maxConcurrentUsers || '100+'} Users</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-mobile-alt"></i>
                                    <div>
                                        <span className="info-label">Device Limit</span>
                                        <span className="info-value">{viewingBranch.limits?.maxDevicesPerUser || '3'} / User</span>
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <span className="info-label">Login Methods</span>
                                <div style={{ marginTop: '0.5rem' }}>{methodBadges(viewingBranch)}</div>
                            </div>
                        </div>
                        <div className="modal-footer-modern" style={{ justifyContent: 'space-between' }}>
                            <button className="btn-secondary-modern" onClick={() => handleGenerateKeys(viewingBranch._id)}>
                                <i className="fas fa-key"></i> Generate API Keys
                            </button>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn-secondary-modern" onClick={() => setViewingBranch(null)}>Close</button>
                                <button className="btn-primary-modern" onClick={() => { const b = viewingBranch; setViewingBranch(null); handleEdit(b); }}>
                                    <i className="fas fa-edit"></i> Edit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{editingBranch ? 'Edit Branch' : 'Create New Branch'}</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="modal-tabs">
                            <button type="button" className={`modal-tab-btn ${modalTab === 'general' ? 'active' : ''}`} onClick={() => setModalTab('general')}>
                                <i className="fas fa-info-circle"></i> General
                            </button>
                            <button type="button" className={`modal-tab-btn ${modalTab === 'portal' ? 'active' : ''}`} onClick={() => setModalTab('portal')}>
                                <i className="fas fa-palette"></i> Portal & Branding
                            </button>
                            <button type="button" className={`modal-tab-btn ${modalTab === 'limits' ? 'active' : ''}`} onClick={() => setModalTab('limits')}>
                                <i className="fas fa-sliders-h"></i> Limits
                            </button>
                            <button type="button" className={`modal-tab-btn ${modalTab === 'integrations' ? 'active' : ''}`} onClick={() => setModalTab('integrations')}>
                                <i className="fas fa-plug"></i> Integrations
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                {modalTab === 'general' && (
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-server"></i> Basic Information
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>Tenant <span className="text-red-600">*</span></label>
                                                <select
                                                    name="tenantId"
                                                    value={formData.tenantId}
                                                    onChange={handleInputChange}
                                                    required
                                                    disabled={admin?.role !== 'superadmin' && formData.tenantId}
                                                >
                                                    <option value="">Select Tenant</option>
                                                    {tenants.map(tenant => (
                                                        <option key={tenant._id} value={tenant._id}>{tenant.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Branch Name <span className="text-red-600">*</span></label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="Main Branch"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Company Name</label>
                                                <input
                                                    type="text"
                                                    name="companyName"
                                                    value={formData.companyName}
                                                    onChange={handleInputChange}
                                                    placeholder="Company LLC"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Branch Code <span className="text-red-600">*</span></label>
                                                <input
                                                    type="text"
                                                    name="code"
                                                    value={formData.code}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="BRANCH01"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Default Plan</label>
                                                <select
                                                    name="defaultPlanId"
                                                    value={formData.defaultPlanId}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Plan</option>
                                                    {plans.map(plan => (
                                                        <option key={plan._id} value={plan._id}>{plan.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Splash Template <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.72rem' }}>(optional)</span></label>
                                                <select
                                                    name="templateId"
                                                    value={formData.templateId}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">None — portal default</option>
                                                    {templates.map(template => (
                                                        <option key={template._id} value={template._id}>{template.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <h3 className="section-title mt-6">
                                            <i className="fas fa-shield-alt"></i> Login Policy
                                        </h3>
                                        <div className="form-group-modern">
                                            <label>Allowed Login Methods</label>
                                            <p className="input-hint" style={{ marginBottom: '10px' }}>
                                                Select every auth method guests can use on the captive portal for this branch.
                                            </p>
                                            <div className="methods-selector" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                                                {[
                                                    { value: 'pms',               label: 'PMS Login',           icon: 'fa-hotel' },
                                                    { value: 'voucher',           label: 'Voucher',             icon: 'fa-ticket-alt' },
                                                    { value: 'access_code',       label: 'Access Code',         icon: 'fa-key' },
                                                    { value: 'username_password', label: 'Username & Password', icon: 'fa-user-lock' },
                                                    { value: 'sms_otp',           label: 'SMS OTP',             icon: 'fa-sms' },
                                                    { value: 'whatsapp_otp',      label: 'WhatsApp OTP',        icon: 'fa-whatsapp' },
                                                    { value: 'email_otp',         label: 'Email OTP',           icon: 'fa-envelope' },
                                                    { value: 'qr',                label: 'QR Login',            icon: 'fa-qrcode' },
                                                    { value: 'mac',               label: 'MAC Auth (bypass)',   icon: 'fa-network-wired' },
                                                    { value: 'social',            label: 'Social Login',        icon: 'fa-share-alt' },
                                                    // The portal has these sign-in forms (residents / staff an admin added under Users).
                                                    { value: 'resident',          label: 'Resident',            icon: 'fa-house-user' },
                                                    { value: 'staff',             label: 'Staff',               icon: 'fa-id-badge' },
                                                ].map(({ value, label, icon }) => {
                                                    const checked = formData.loginPolicy.methods.includes(value);
                                                    return (
                                                        <label key={value} style={{
                                                            display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                                                            padding: '8px 12px', background: checked ? '#ede9fe' : '#f9fafb',
                                                            borderRadius: '8px',
                                                            border: `1px solid ${checked ? '#a855f7' : '#e5e7eb'}`,
                                                            transition: 'all 0.15s',
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => handleMethodToggle(value)}
                                                                style={{ accentColor: '#a855f7' }}
                                                            />
                                                            <i className={`fas ${icon}`} style={{ color: checked ? '#7c3aed' : '#9ca3af', width: '16px', textAlign: 'center' }}></i>
                                                            <span style={{ fontSize: '0.85rem', fontWeight: checked ? 600 : 400, color: checked ? '#5b21b6' : '#374151' }}>{label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Tab order + label overrides — only the three methods the portal
                                            actually renders as tabs today (voucher/pms/otp). Purely cosmetic:
                                            doesn't change which methods are enabled, just how their tabs look
                                            and in what order they appear on the captive portal. */}
                                        {(() => {
                                            const PORTAL_TABS = { voucher: 'Voucher', pms: 'PMS / Room Login', otp: 'OTP Login' };
                                            // A tab appears only for a box ticked above (OTP: SMS OTP or WhatsApp OTP).
                                            const TAB_OF = { voucher: ['voucher'], pms: ['pms'], otp: ['sms_otp', 'whatsapp_otp'] };
                                            const ticked = formData.loginPolicy.methods;
                                            const enabledPortalTabs = Object.keys(PORTAL_TABS).filter(m => TAB_OF[m].some(v => ticked.includes(v)));
                                            if (enabledPortalTabs.length === 0) return null;

                                            const currentOrder = formData.loginPolicy.methodOrder?.length
                                                ? formData.loginPolicy.methodOrder.filter(m => enabledPortalTabs.includes(m))
                                                : [];
                                            const orderedTabs = [
                                                ...currentOrder,
                                                ...enabledPortalTabs.filter(m => !currentOrder.includes(m)),
                                            ];

                                            const moveTab = (index, delta) => {
                                                const next = [...orderedTabs];
                                                const target = index + delta;
                                                if (target < 0 || target >= next.length) return;
                                                [next[index], next[target]] = [next[target], next[index]];
                                                setFormData(p => ({ ...p, loginPolicy: { ...p.loginPolicy, methodOrder: next } }));
                                            };

                                            return (
                                                <div className="form-group-modern full-width">
                                                    <label>Tab Order &amp; Labels</label>
                                                    <p className="input-hint" style={{ marginBottom: '10px' }}>
                                                        Controls the order these appear as tabs on the captive portal, and lets you override their default label.
                                                    </p>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                        {orderedTabs.map((m, i) => (
                                                            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                    <button type="button" onClick={() => moveTab(i, -1)} disabled={i === 0}
                                                                        style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, padding: 0, lineHeight: 1 }}>
                                                                        <i className="fas fa-chevron-up"></i>
                                                                    </button>
                                                                    <button type="button" onClick={() => moveTab(i, 1)} disabled={i === orderedTabs.length - 1}
                                                                        style={{ background: 'none', border: 'none', cursor: i === orderedTabs.length - 1 ? 'default' : 'pointer', opacity: i === orderedTabs.length - 1 ? 0.3 : 1, padding: 0, lineHeight: 1 }}>
                                                                        <i className="fas fa-chevron-down"></i>
                                                                    </button>
                                                                </div>
                                                                <span style={{ fontSize: '0.8rem', color: '#6b7280', width: 90, flexShrink: 0 }}>{PORTAL_TABS[m]}</span>
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Default: "${PORTAL_TABS[m]}"`}
                                                                    value={formData.loginPolicy.methodLabels?.[m] || ''}
                                                                    onChange={e => setFormData(p => ({
                                                                        ...p,
                                                                        loginPolicy: {
                                                                            ...p.loginPolicy,
                                                                            methodLabels: { ...p.loginPolicy.methodLabels, [m]: e.target.value },
                                                                        },
                                                                    }))}
                                                                    style={{ flex: 1 }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {formData.loginPolicy.methods.includes('voucher') && (
                                            <div className="form-group-modern">
                                                <label>Voucher Login UX</label>
                                                <select
                                                    name="loginPolicy.voucherMode"
                                                    value={formData.loginPolicy.voucherMode}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="tab">Same Page — Voucher tab alongside other login methods</option>
                                                    <option value="separate">Separate Page — dedicated voucher-only login screen</option>
                                                </select>
                                                <p className="input-hint">Controls how voucher entry is presented on the captive portal.</p>
                                            </div>
                                        )}

                                        {formData.loginPolicy.methods.includes('otp') && (
                                            <div className="form-group-modern">
                                                <label>Default OTP Channel</label>
                                                <select
                                                    name="loginPolicy.otpDefaultMethod"
                                                    value={formData.loginPolicy.otpDefaultMethod}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="whatsapp">WhatsApp (guest can switch to SMS)</option>
                                                    <option value="sms">SMS (guest can switch to WhatsApp)</option>
                                                </select>
                                                <p className="input-hint">Pre-selected channel on the captive portal's OTP screen for this branch. Guest can still toggle to the other option.</p>
                                            </div>
                                        )}

                                        {formData.loginPolicy.methods.includes('otp') && (() => {
                                            const limit = formData.loginPolicy.otpMaxDevices;
                                            const limited = limit !== null;
                                            const setLimit = (value) => handleInputChange({ target: { name: 'loginPolicy.otpMaxDevices', value, type: 'text' } });
                                            return (
                                                <div className="form-group-modern">
                                                    <label>Devices Per Mobile Number</label>
                                                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 400 }}>
                                                            <input type="radio" name="otpDeviceLimit" checked={!limited} onChange={() => setLimit(null)} />
                                                            Unlimited
                                                        </label>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 400 }}>
                                                            <input type="radio" name="otpDeviceLimit" checked={limited} onChange={() => setLimit(1)} />
                                                            Limited
                                                        </label>
                                                    </div>
                                                    {limited && (
                                                        <input
                                                            type="number"
                                                            aria-label="Devices per mobile number"
                                                            name="loginPolicy.otpMaxDevices"
                                                            min="1"
                                                            max="20"
                                                            value={limit ?? 1}
                                                            onChange={handleInputChange}
                                                            style={{ maxWidth: '8rem' }}
                                                        />
                                                    )}
                                                    <p className="input-hint">
                                                        {limited
                                                            ? `One mobile number can be online on at most ${limit || 1} device(s) at the same time — WhatsApp and SMS OTP alike. Another device is asked to sign one out first.`
                                                            : 'Every device that signs in with the number\'s OTP is let on (no cap).'}
                                                    </p>
                                                </div>
                                            );
                                        })()}

                                        {(() => {
                                            const pre = { ...PRELOGIN_DEFAULTS, ...(formData.loginPolicy.preLogin || {}) };
                                            const setPre = (key, value) => setFormData((prev) => ({
                                                ...prev,
                                                loginPolicy: { ...prev.loginPolicy, preLogin: { ...PRELOGIN_DEFAULTS, ...(prev.loginPolicy.preLogin || {}), [key]: value } },
                                            }));
                                            return (
                                                <div className="form-group-modern">
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!pre.enabled}
                                                            onChange={(e) => setPre('enabled', e.target.checked)}
                                                            style={{ accentColor: '#a855f7' }}
                                                        />
                                                        Internet Before Sign-in (no mobile network)
                                                    </label>
                                                    <p className="input-hint">
                                                        Where phones have no mobile network: as a guest's device connects (the sign-in page opens),
                                                        it gets a few slow minutes of internet — so the WhatsApp code can arrive and a UPI app can pay.
                                                        If they run out, asking for the code or paying opens them again (within the daily number).
                                                        A timed window per device needs MikroTik with Router API. Nomadix / Ruijie can't open one — their Push Config lists WhatsApp for the gateway's allow list instead (every guest, no time limit). Run Push Config on the router after changing this.
                                                    </p>
                                                    {pre.enabled && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))', gap: '0.75rem' }}>
                                                            <label style={{ fontWeight: 400 }}>
                                                                Minutes each time
                                                                <input type="number" min="1" max="30" value={pre.minutes} onChange={(e) => setPre('minutes', e.target.value)} />
                                                            </label>
                                                            <label style={{ fontWeight: 400 }}>
                                                                Speed (kbps)
                                                                <input type="number" min="64" max="10240" step="64" value={pre.speedKbps} onChange={(e) => setPre('speedKbps', e.target.value)} />
                                                            </label>
                                                            <label style={{ fontWeight: 400 }}>
                                                                Times per device a day
                                                                <input type="number" min="1" max="10" value={pre.perDay} onChange={(e) => setPre('perDay', e.target.value)} />
                                                            </label>
                                                        </div>
                                                    )}
                                                    {pre.enabled && (
                                                        <p className="input-hint">
                                                            {`Each time: ${pre.minutes || 5} min at ${pre.speedKbps || 1024} kbps, up to ${pre.perDay || 3} time(s) per device in 24 hours (on connect, then again for the code or the payment if it ran out). Signing in ends it at once.`}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {(() => {
                                            const ticked = normalizeMethods(formData.loginPolicy.methods);
                                            const rows = METHOD_PLAN_ROWS.filter(r => ticked.includes(r.method));
                                            const elsewhere = METHOD_PLAN_ELSEWHERE
                                                .map(e => ({ ...e, on: e.methods.filter(m => ticked.includes(m)) }))
                                                .filter(e => e.on.length);
                                            if (!rows.length && !elsewhere.length) return null;
                                            const chosen = methodPlansOf(formData.loginPolicy.methodPlans);
                                            const setPlan = (key, value) => setFormData((prev) => ({
                                                ...prev,
                                                loginPolicy: { ...prev.loginPolicy, methodPlans: { ...methodPlansOf(prev.loginPolicy.methodPlans), [key]: value } },
                                            }));
                                            const activePlans = (plans || []).filter(p => p.status !== 'inactive' && p.status !== 'archived');
                                            return (
                                                <div className="form-group-modern">
                                                    <label>Plan per login method</label>
                                                    {rows.length > 0 && (!editingBranch ? (
                                                        <p className="input-hint">Save the branch first, then add its plans — they can be chosen here.</p>
                                                    ) : (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.75rem' }}>
                                                            {rows.map(r => (
                                                                <label key={r.key} style={{ fontWeight: 400 }}>
                                                                    {r.label}
                                                                    <select
                                                                        aria-label={`Plan for ${r.label}`}
                                                                        value={chosen[r.key]}
                                                                        onChange={(e) => setPlan(r.key, e.target.value)}
                                                                    >
                                                                        <option value="">As now ({r.asNow})</option>
                                                                        {activePlans.map(p => (
                                                                            <option key={p._id} value={p._id}>{p.name}{planSpeed(p)}</option>
                                                                        ))}
                                                                    </select>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    ))}
                                                    {elsewhere.length > 0 && (
                                                        <ul className="input-hint" style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
                                                            {elsewhere.map(e => (
                                                                <li key={e.label}><strong>{e.label}:</strong> {e.note}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <p className="input-hint">
                                                        A guest gets the plan of the method they signed in with — its speed, validity and devices.
                                                        A plan set by hand still comes first: a room&apos;s own plan, and a voucher always uses the plan of its batch.
                                                        Applies from the next sign-in.
                                                    </p>
                                                </div>
                                            );
                                        })()}

                                        {(() => {
                                            const limits = { ...SESSION_LIMIT_DEFAULTS, ...(formData.loginPolicy.sessionLimits || {}) };
                                            const setLimit = (key, value) => setFormData((prev) => ({
                                                ...prev,
                                                loginPolicy: { ...prev.loginPolicy, sessionLimits: { ...SESSION_LIMIT_DEFAULTS, ...(prev.loginPolicy.sessionLimits || {}), [key]: value } },
                                            }));
                                            const permanent = String(limits.sessionMinutes) === '0' && String(limits.idleMinutes) === '0';
                                            return (
                                                <div className="form-group-modern">
                                                    <label>Session Limits</label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 400, marginBottom: '0.5rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={permanent}
                                                            onChange={(e) => {
                                                                const next = e.target.checked ? { sessionMinutes: 0, idleMinutes: 0 } : { ...SESSION_LIMIT_DEFAULTS };
                                                                setFormData((prev) => ({ ...prev, loginPolicy: { ...prev.loginPolicy, sessionLimits: next } }));
                                                            }}
                                                            style={{ accentColor: '#a855f7' }}
                                                        />
                                                        Permanent session — stay online until the plan or stay ends
                                                    </label>
                                                    {!permanent && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', gap: '0.75rem' }}>
                                                            <label style={{ fontWeight: 400 }}>
                                                                Log out after (minutes) — traffic or not
                                                                <input type="number" min="0" max="43200" placeholder="Router default" aria-label="Log out after (minutes)" value={limits.sessionMinutes} onChange={(e) => setLimit('sessionMinutes', e.target.value)} />
                                                            </label>
                                                            <label style={{ fontWeight: 400 }}>
                                                                Log out when idle for (minutes) — no traffic
                                                                <input type="number" min="0" max="1440" placeholder="Router default" aria-label="Log out when idle for (minutes)" value={limits.idleMinutes} onChange={(e) => setLimit('idleMinutes', e.target.value)} />
                                                            </label>
                                                        </div>
                                                    )}
                                                    <p className="input-hint">
                                                        {permanent
                                                            ? 'A signed-in device is never signed out for time or inactivity — only when its plan (validity) or hotel stay ends, or it signs out.'
                                                            : '"Log out after" ends the session at that time whether the guest is using the Wi-Fi or not; "idle" only after that long without traffic. Blank = the router\'s own setting (24 h / 10 min unless changed). 0 = no limit. A plan with its own RADIUS timeouts overrides this.'}
                                                        {' '}When it ends, the guest sees the sign-in page again. Applies to every router of this branch from the next sign-in — no Push Config needed.
                                                    </p>
                                                </div>
                                            );
                                        })()}

                                        <div className="form-group-modern">
                                            <label>Auto Check-out — Manual Check-ins (hours)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                name="roomOps.autoCheckoutManualHours"
                                                placeholder="Disabled"
                                                value={formData.roomOps?.autoCheckoutManualHours ?? ''}
                                                onChange={handleInputChange}
                                            />
                                            <p className="input-hint">
                                                Automatically check out a room this many hours after a manual (reception) check-in, even with no planned checkout date — useful for short-stay/day-pass check-ins. Leave blank to disable.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {modalTab === 'portal' && (
                                    <div className="portal-config-grid">
                                        <div className="branding-section">
                                            <h4 className="section-subtitle">Visual Branding</h4>
                                            <div className="logo-uploads">
                                                <LogoUpload
                                                    title="Branch Logo"
                                                    currentLogo={formData.branding?.logo?.url}
                                                    onUpload={handleLogoUpload}
                                                    showPreview={true}
                                                />
                                                <LogoUpload
                                                    title="Portal Background"
                                                    currentLogo={formData.branding?.backgroundImage?.url}
                                                    onUpload={handleBackgroundUpload}
                                                    aspectRatio="wide"
                                                    showPreview={true}
                                                />
                                            </div>
                                            <div className="color-pickers">
                                                <div className="form-group-modern">
                                                    <label>Primary Color</label>
                                                    <div className="color-input-wrapper">
                                                        <input
                                                            type="color"
                                                            name="branding.primaryColor"
                                                            value={formData.branding.primaryColor}
                                                            onChange={handleInputChange}
                                                        />
                                                        <span>{formData.branding.primaryColor}</span>
                                                    </div>
                                                </div>
                                                <div className="form-group-modern">
                                                    <label>Secondary Color</label>
                                                    <div className="color-input-wrapper">
                                                        <input
                                                            type="color"
                                                            name="branding.secondaryColor"
                                                            value={formData.branding.secondaryColor}
                                                            onChange={handleInputChange}
                                                        />
                                                        <span>{formData.branding.secondaryColor}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="messages-section">
                                            <h4 className="section-subtitle">Portal Content</h4>
                                            <div className="form-group-modern">
                                                <label>Welcome Message</label>
                                                <input
                                                    type="text"
                                                    name="welcomeMessage"
                                                    value={formData.welcomeMessage}
                                                    onChange={handleInputChange}
                                                    placeholder="Welcome to our network"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Redirect URL (After Login)</label>
                                                <input
                                                    type="text"
                                                    name="redirectURL"
                                                    value={formData.redirectURL}
                                                    onChange={handleInputChange}
                                                    placeholder="https://google.com"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Terms & Conditions (Optional)</label>
                                                <textarea
                                                    name="termsAndConditions"
                                                    value={formData.termsAndConditions}
                                                    onChange={handleInputChange}
                                                    rows="5"
                                                    placeholder="Enter your terms and conditions here..."
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {modalTab === 'limits' && (
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-sliders-h"></i> Operational Limits
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label>Max Concurrent Users</label>
                                                <input
                                                    type="number"
                                                    name="limits.maxConcurrentUsers"
                                                    value={formData.limits.maxConcurrentUsers}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="form-group-modern">
                                                <label>Max Devices Per User</label>
                                                <input
                                                    type="number"
                                                    name="limits.maxDevicesPerUser"
                                                    value={formData.limits.maxDevicesPerUser}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {modalTab === 'integrations' && (
                                    <div className="form-section">
                                        <h3 className="section-title" style={{ margin: '0 0 1rem' }}>
                                            <i className="fas fa-plug"></i> Integration Hub
                                        </h3>
                                        <PmsProviderFields
                                            formData={formData}
                                            handleInputChange={handleInputChange}
                                            modalBranchPages={modalBranchPages}
                                            admin={admin}
                                            loadingDefaults={loadingDefaults}
                                            onLoadDefaults={loadSystemDefaults}
                                            branchId={editingBranch?._id}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern">
                                    <i className="fas fa-save"></i>
                                    {editingBranch ? 'Update Branch' : 'Create Branch'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div>
                                <h2>Delete Branch</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="warning-box" style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#b91c1c', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Warning</strong>
                                </div>
                                <p style={{ color: '#7f1d1d' }}>Are you sure you want to delete <strong className="text-gray-900">{branchToDelete?.name}</strong>?</p>
                                <p style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '0.5rem' }}>
                                    This will remove all associated configurations. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i>
                                Delete Branch
                            </button>
                        </div>
                    </div>
                </div>
            )
            }
        </div >
    );
};

export default Branches;
